import React from "react";
import { withStyles } from "@material-ui/core/styles";
import { Grid, Typography, Paper, CardMedia, CircularProgress } from "@material-ui/core";
import { useQuery } from "@apollo/client";
import moment from "moment";
import EventCard from "../Events/EventCard";
import CreateDialog from "../../../components/common/CreateDialog";
import pastEventsV2Query from "../../../graphql/reports/past-events-v2";
import CurrentCompanyContext from "../../../containers/CurrentCompany/CurrentCompanyContext";
import logo from "../../../Assets/logo.png";

const styles = (theme) => ({
  root: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    minWidth: 650,
  },
  empty: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "8px 8px",
    height: 400,
    flex: 1,
  },
  emptyText: {
    fontWeight: 400,
    color: "#75D6B2",
    marginTop: 16,
  },
  header: {
    display: "flex",
    textAlign: "center",
    width: "100%",
    marginBottom: 32,
    justifyContent: "center",
    position: "relative",
  },
});

const EventsCompleted = ({ disableCreateEvent, classes, refetch }) => {
  const { currentCompany } = React.useContext(CurrentCompanyContext);

  // Server-side window: the backend filters to the last 3 months on the
  // { company, dateEnd } index and projects only the fields EventCard needs —
  // instead of shipping the company's entire events array and filtering in
  // the browser (previous behavior).
  // errorPolicy "all" so a GraphQL error is visible instead of silently
  // rendering "no events" (the client's watchQuery default is "ignore").
  const { loading, error, data } = useQuery(pastEventsV2Query, {
    variables: {
      companyId: currentCompany ? currentCompany._id : null,
      monthsBack: 3,
    },
    skip: !currentCompany,
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  const pastEvents = (data && data.pastEventsV2) || [];

  let sortedEvents = [
    {
      startOfWeek: moment().startOf("week"),
      endOfWeek: moment().endOf("week"),
      events: [],
    },
  ];
  pastEvents.forEach((event) => {
    // If the event belongs to an existing week bucket, add it there.
    for (let i = 0; i < sortedEvents.length; i++) {
      const { startOfWeek, endOfWeek } = sortedEvents[i];
      if (moment(event.date).isBetween(startOfWeek, endOfWeek)) {
        sortedEvents[i].events.push(event);
        return;
      }
    }
    // Otherwise create a new week bucket for it.
    sortedEvents.push({
      startOfWeek: moment(event.date).startOf("week"),
      endOfWeek: moment(event.date).endOf("week"),
      events: [event],
    });
  });

  sortedEvents = sortedEvents.sort((weekA, weekB) =>
    moment(weekA.startOfWeek).isBefore(weekB.startOfWeek) ? 1 : -1
  );

  // sort the events by date within week
  const sortedByDate = sortedEvents.map((weeklyEventsArray) => {
    const sortedWithinWeek = weeklyEventsArray.events.sort((a, b) =>
      moment(a.date).isAfter(b.date)
        ? -1
        : moment(a.date).isAfter(b.date)
        ? 1
        : 0
    );
    return { ...weeklyEventsArray, events: sortedWithinWeek };
  });

  if (loading) {
    return (
      <div style={{ width: "100%", textAlign: "center", padding: "48px 0" }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    console.error("pastEventsV2 query error:", error);
    // Only take over the page when nothing rendered; a partial result still wins.
    if (pastEvents.length === 0) {
      return (
        <Paper className={classes.empty}>
          <div style={{ textAlign: "center" }}>
            <Typography variant="h5" style={{ color: "#c0392b" }}>
              Could not load past events. Please try refreshing the page.
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {String((error && error.message) || error)}
            </Typography>
          </div>
        </Paper>
      );
    }
  }

  return (
    <div className={classes.grid}>
      {!disableCreateEvent && (
        <div className={classes.header}>
          <CreateDialog type="event" action="create" />
          <Typography gutterBottom variant="h4">
            {`${!!currentCompany && currentCompany.name} Events`}
          </Typography>
        </div>
      )}
      {sortedByDate[0].events.length > 0 && (
        <Typography
          gutterBottom
          variant="h5"
          style={{
            paddingLeft: 8,
            borderBottom: "1px solid black",
            borderColor: "#00f2c3",
          }}
        >
          This Week
        </Typography>
      )}
      <Grid container spacing={2} style={{ marginBottom: 0, marginTop: 0 }}>
        {pastEvents.length < 1 && !disableCreateEvent && (
          <Paper className={classes.empty}>
            <div style={{ textAlign: "center " }}>
              <CardMedia
                style={{ width: 200, height: 200, margin: "auto" }}
                image={logo}
              />
              <Typography variant="h4" className={classes.emptyText}>
                There are no events within the last 3 months for this company
              </Typography>
            </div>
          </Paper>
        )}
        {pastEvents.length < 1 && disableCreateEvent ? (
          <Paper className={classes.empty}>
            <div style={{ textAlign: "center " }}>
              <CardMedia
                style={{ width: 200, height: 200, margin: "auto" }}
                image={logo}
              />
              <Typography variant="h4" className={classes.emptyText}>
                No events within the last 3 months are assigned to you at this
                time
              </Typography>
            </div>
          </Paper>
        ) : (
          sortedByDate[0].events.map((event, index) => (
            <Grid item key={event._id || index} xs={12} sm={6} md={4} lg={4}>
              <EventCard
                event={event}
                title={event.title}
                instructor={event.instructor}
                location={event.location}
                date={event.date}
                totalSpots={event.totalSpots}
                takenSpots={Math.min((event.users || []).length, event.totalSpots)}
                description={event.description}
                image={event.image}
                refetch={refetch}
                id={event._id}
              />
            </Grid>
          ))
        )}
      </Grid>
      {sortedByDate.slice(1).map((week, index) => (
        <div
          key={week.startOfWeek ? week.startOfWeek.valueOf() : index}
          style={{ marginBottom: 24 }}
        >
          <Typography
            gutterBottom
            variant="h5"
            style={{
              paddingLeft: 8,
              borderBottom: "1px solid black",
              borderColor: "#00f2c3",
            }}
          >
            {moment().diff(week.endOfWeek, "weeks") === -1
              ? "Last Week"
              : `${moment().diff(week.startOfWeek, "weeks")} Weeks ago`}
          </Typography>
          <Grid
            container
            spacing={2}
            style={{
              marginTop: 8,
            }}
          >
            {week.events.map((event, index) => (
              <Grid item key={event._id || `w${index}`} xs={12} sm={6} md={4} lg={4}>
                <EventCard
                  event={event}
                  title={event.title}
                  instructor={event.instructor}
                  location={event.location}
                  date={event.date}
                  totalSpots={event.totalSpots}
                  takenSpots={Math.min((event.users || []).length, event.totalSpots)}
                  description={event.description}
                  image={event.image}
                  id={event._id}
                  refetch={refetch}
                />
              </Grid>
            ))}
          </Grid>
        </div>
      ))}
    </div>
  );
};

export default withStyles(styles)(EventsCompleted);
